import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(...inputs))
}

// 将后端可能为 JSON字符串数组/逗号分隔字符串/真数组/null 的字段统一归一化为字符串数组
export function toStringArray(value: unknown): string[] {
  const normalizeItem = (item: unknown): string[] => {
    if (item === null || item === undefined || item === false) return []
    if (Array.isArray(item)) return item.flatMap(normalizeItem)
    if (typeof item === 'object') {
      const data = item as Record<string, unknown>
      for (const key of ['url', 'src', 'path', 'href', 'image', 'image_url', 'imageUrl', 'name', 'title', 'label', 'text', 'value']) {
        const parsed = normalizeItem(data[key])
        if (parsed.length) return parsed
      }
      return ['images', 'photos', 'files', 'attachments', 'items', 'list', 'data'].flatMap(key => normalizeItem(data[key]))
    }
    return [String(item)]
  }

  if (Array.isArray(value)) return value.flatMap(normalizeItem).map(item => item.trim()).filter(Boolean)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return []
    if (trimmed.startsWith('[')) {
      try {
        const arr = JSON.parse(trimmed)
        if (Array.isArray(arr)) return arr.flatMap(normalizeItem).map(item => item.trim()).filter(Boolean)
      } catch {
        // 解析失败，按分隔符处理
      }
    }
    return trimmed.split(/[，,、\s]+/).filter(Boolean)
  }
  return []
}

export function formatDate(d: string | null | undefined): string {
  if (!d) return '';
  try {
    const date = new Date(d);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  } catch { return d.slice(0, 19).replace('T', ' '); }
}

export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return String(dateStr).slice(0, 19).replace('T', ' ');
  }
}

export function decodeHtmlText(value: string): string {
  if (!value) return '';
  let result = value.trim();
  if (result.startsWith('"') && result.endsWith('"')) {
    try {
      const parsed = JSON.parse(result);
      if (typeof parsed === 'string') result = parsed;
    } catch {
      // 保持原内容
    }
  }
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    for (let i = 0; i < 3; i += 1) {
      textarea.innerHTML = result;
      const decoded = textarea.value;
      if (decoded === result) break;
      result = decoded;
    }
    return result;
  }
  return result
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([\da-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)));
}

export function htmlToPlainText(value: unknown, maxLength?: number): string {
  let result = decodeHtmlText(String(value ?? ''));
  if (!result) return '';
  result = result
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<\/?(p|div|section|article|br|li|tr|h[1-6])\b[^>]*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ');
  result = decodeHtmlText(result).replace(/\s+/g, ' ').trim();
  return maxLength && result.length > maxLength ? `${result.slice(0, maxLength)}...` : result;
}

export function sanitizeHtml(html: string): string {
  if (!html) return '';
  let result = decodeHtmlText(html);
  // 1. 移除 script 标签及其内容
  result = result.replace(/<script[\s\S]*?>\s*[\s\S]*?<\/script>/gi, '');
  result = result.replace(/<script\b[^>]*>/gi, '');
  result = result.replace(/<\/script>/gi, '');
  // 2. 移除 iframe/object/embed 等危险标签
  result = result.replace(/<(iframe|object|embed|form|input|textarea|button|select)[\s\S]*?>\s*[\s\S]*?<\/\1>/gi, '');
  result = result.replace(/<(iframe|object|embed|form|input|textarea|button|select)\b[^>]*\/?>/gi, '');
  // 3. 移除所有 onxxx 事件处理器
  result = result.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
  result = result.replace(/\son\w+\s*=\s*[^>\s]*/gi, '');
  // 4. 移除 javascript: 伪协议
  result = result.replace(/javascript:/gi, '');
  result = result.replace(/javascript&colon;/gi, '');
  result = result.replace(/javascript&#58;/gi, '');
  // 5. 移除 data: 伪协议（可能导致XSS）
  result = result.replace(/data:/gi, '');
  // 6. 移除 vbscript: 伪协议
  result = result.replace(/vbscript:/gi, '');
  // 7. 移除 expression: (IE CSS)
  result = result.replace(/expression\(/gi, '');
  // 8. 移除 style 标签中的 @import 和 expression
  result = result.replace(/<style[\s\S]*?>\s*[\s\S]*?<\/style>/gi, (match) => {
    return match.replace(/@import/gi, '').replace(/expression\(/gi, '');
  });
  // 9. 移除 meta/link/base 等标签
  result = result.replace(/<(meta|link|base)\b[^>]*\/?>/gi, '');
  return result;
}
